import React from 'react';
import { useSelector } from 'react-redux'
import { useApplication } from '@/context';
import QRCode from 'qrcode';
import { reportEvent } from '@/shared/helpers/report-event.helper';
import { LogType } from '@/shared/types/logType';
import { useSearchParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import VendorUi from './VendorUi';
import QRLiveness from './QRLiveness';
import ExitView from './ExitView';
import { buildCreateSessionBody } from './buildCreateSessionBody';


interface LivenessInitialProps {
    vendor: string;
    liveness_session: string;
    person_uuid: string;
    vendor_data?: any;
    trace_id?: string;
}

interface LivenessWrapperState {
    data: LivenessInitialProps | null;
    loading: boolean;
    error: Error | null;
}

interface LivenessWrapperProps {
    callBack: (response: any) => void;
    personUuid: string;
}

function detectMobile(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const isSmallScreen =
        typeof window.matchMedia === 'function'
            ? window.matchMedia('(max-width: 768px)').matches
            : window.innerWidth <= 768;
    const userAgent = navigator.userAgent || (navigator as any).vendor || '';
    return /android|iphone|ipad|ipod|windows phone/i.test(userAgent) || isSmallScreen;
}

/**
 * Convert any value to an Error object with a meaningful message
 */
function ensureError(error: unknown, defaultMessage: string = 'An error occurred'): Error {
    if (error instanceof Error) {
        return error;
    }
    if (typeof error === 'string') {
        return new Error(error);
    }
    if (error && typeof error === 'object' && 'message' in error) {
        return new Error((error as any).message);
    }
    return new Error(defaultMessage);
}
export default function LivenessWrapper(props: LivenessWrapperProps) {
    const [state, setState] = React.useState<LivenessWrapperState>({
        data: null,
        loading: true,
        error: null
    });
    const liveness = useSelector((state: any) => state.auth.value);
    const { authenticationUseCase } = useApplication();
    const hasFetched = React.useRef(false);
    const hasProcessedLiveness = React.useRef(false);
    const hasRetried = React.useRef(false);
    const [livenessData, setLivenessData] = React.useState({});
    // Lazy initializer: detect mobile synchronously on the first render so
    // the fetch effect and its .then() closure capture the correct value
    // immediately — avoiding the race where isMobile starts as false and the
    // fetch sends `source` (or generates the QR code) on real mobile devices.
    const [isMobile, setIsMobile] = React.useState(detectMobile);
    const [qrCodeUrl, setQrCodeUrl] = React.useState<string>('');
    const [qrLinkUrl, setQrLinkUrl] = React.useState<string>('');
    const [showExitView, setShowExitView] = React.useState(false);
    const [exitViewIsError, setExitViewIsError] = React.useState(false);
    const [searchParams] = useSearchParams();
    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const isPollingRef = React.useRef(false);
    // Set to true only when the session was created as a desktop QR flow
    // (i.e. `source` was registered with the server and a QR code was generated).
    // Guards the polling branch in validateCompletion so a resize-triggered
    // isMobile flip cannot invoke deviceVerificationChallenge on mobile sessions.
    const isQrFlowRef = React.useRef(false);
    const [sourceId] = React.useState<string>(uuidv4());
    const LIVENESS_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
    const lastReportedErrorRef = React.useRef<Error | null>(null);

    // Single reporting point for every error this component can set (no-UUID,
    // session fetch/init failures, timeout, QR generation, AWS retry) — new
    // error branches are covered automatically without adding a call at each
    // `setState` site.
    React.useEffect(() => {
        if (!state.error || state.error === lastReportedErrorRef.current) return;
        lastReportedErrorRef.current = state.error;
        reportEvent('LIVENESS_WRAPPER_ERROR', {
            event: 'LIVENESS_WRAPPER_ERROR',
            message: state.error.message,
            personUuid: props.personUuid,
            timestamp: new Date().toISOString(),
        }, LogType.ERROR);
    }, [state.error, props.personUuid]);

    React.useEffect(() => {
        // Re-evaluate on resize (e.g. browser chrome appears/disappears).
        // The initial value is already set correctly via the lazy useState
        // initializer above, so we only need to update on subsequent resizes.
        const handleResize = () => setIsMobile(detectMobile());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const validateCompletion = React.useCallback(() => {
        if (!state.data) return;
        if (hasProcessedLiveness.current) return;
        if ((!liveness || (!liveness.liveness && !liveness.data)) && isMobile) return;
        if (Object.keys(livenessData).length > 0) return;
        const isQrSecondaryView = searchParams.get('source') === 'qr';

        if (liveness.data) {
            hasProcessedLiveness.current = true;
            setLivenessData(liveness.data);
            clearLivenessTimeout();
            authenticationUseCase.personsVerificationChallenge(state.data.vendor, state.data.liveness_session, liveness.data).then((result) => {
                const hasTransaction = result && result.transaction_id;
                reportEvent('LIVENESS_SESSION_COMPLETED', {
                    event: 'LIVENESS_SESSION_COMPLETED',
                    personUuid: props.personUuid,
                    transactionId: result?.transaction_id ?? null,
                    vendor: state.data?.vendor,
                    livenessSession: state.data?.liveness_session,
                    hasTransaction: !!hasTransaction,
                    timestamp: new Date().toISOString(),
                }, LogType.INFO);
                if (isQrSecondaryView) {
                    setExitViewIsError(!hasTransaction);
                    setShowExitView(true);
                } else {
                    props.callBack(result);
                }
            }).catch((error) => {
                console.error("Error fetching verification challenge:", error);
                reportEvent('LIVENESS_VERIFICATION_CHALLENGE_ERROR', {
                    event: 'LIVENESS_VERIFICATION_CHALLENGE_ERROR',
                    personUuid: props.personUuid,
                    errorMessage: error?.message,
                    vendor: state.data?.vendor,
                    livenessSession: state.data?.liveness_session,
                    timestamp: new Date().toISOString(),
                }, LogType.ERROR);
                if (isQrSecondaryView) {
                    setExitViewIsError(true);
                    setShowExitView(true);
                } else {
                    props.callBack({ result: 'ERROR', error_message: error.message });
                }
            });
        } else {
            // Only poll when this session was explicitly set up as a cross-device
            // QR flow. Prevents a resize-triggered isMobile flip from calling
            // deviceVerificationChallenge on a mobile-only session.
            if (!isQrFlowRef.current) return;
            if (isPollingRef.current) return;
            isPollingRef.current = true;
            authenticationUseCase.deviceVerificationChallenge(props.personUuid, sourceId, state.data.liveness_session).then((result) => {
                const hasTransaction = result && result.transaction_id;
                if (hasTransaction) {
                    hasProcessedLiveness.current = true;
                    clearLivenessTimeout();
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    reportEvent('LIVENESS_SESSION_COMPLETED', {
                        event: 'LIVENESS_SESSION_COMPLETED',
                        personUuid: props.personUuid,
                        transactionId: result.transaction_id,
                        vendor: state.data?.vendor,
                        livenessSession: state.data?.liveness_session,
                        hasTransaction: true,
                        flow: 'qr',
                        timestamp: new Date().toISOString(),
                    }, LogType.INFO);
                    props.callBack(result);
                }
            }).catch((error) => {
                // Don't log polling timeout errors since they are expected
                if (error.message !== 'Polling timeout') {
                    console.error("Error fetching verification challenge:", error);
                    reportEvent('LIVENESS_VERIFICATION_CHALLENGE_ERROR', {
                        event: 'LIVENESS_VERIFICATION_CHALLENGE_ERROR',
                        personUuid: props.personUuid,
                        errorMessage: error?.message,
                        vendor: state.data?.vendor,
                        livenessSession: state.data?.liveness_session,
                        flow: 'qr',
                        timestamp: new Date().toISOString(),
                    }, LogType.ERROR);
                }
            }).finally(() => {
                isPollingRef.current = false;
            });
        }
    }, [state.data, liveness, isMobile, livenessData, authenticationUseCase, props, sourceId, searchParams]);

    const onRetryWithAws = React.useCallback(() => {
        if (hasRetried.current) return;
        hasRetried.current = true;
        hasProcessedLiveness.current = false;

        reportEvent('LIVENESS_VENDOR_RETRY', {
            event:      'LIVENESS_VENDOR_RETRY',
            from:       'incode',
            to:         'aws',
            personUuid: props.personUuid,
            timestamp:  new Date().toISOString(),
        }, LogType.INFO);

        setState({ data: null, loading: true, error: null });

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
        if (!API_BASE_URL) {
            setState(prev => ({ ...prev, loading: false, error: new Error('Environment variable REACT_APP_API_BASE_URL is not defined. Please configure it before running the application.') }));
            return;
        }
        // Forward every param the original create-session sent; the only
        // override is preferred_vendor, since this fallback exists to force
        // AWS after Incode threw a non-biometric error.
        const body = buildCreateSessionBody({
            personUuid: props.personUuid,
            isMobile,
            sourceId,
            searchParams,
            overrides: {
                preferred_vendor: 'aws',
            },
        });
        fetch(
            `${API_BASE_URL}/persons/api/liveness`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            }
        )
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`AWS session request failed (${response.status})`);
                }
                return response.json();
            })
            .then((result) => {
                setState({
                    data: {
                        vendor: result.data.vendor,
                        liveness_session: result.data.liveness_session,
                        person_uuid: result.data.person_uuid,
                        vendor_data: result.data.vendor_data,
                        trace_id: result.data.trace_id,
                    },
                    loading: false,
                    error: null,
                });
            })
            .catch((error) => {
                setState(prev => ({ ...prev, loading: false, error: ensureError(error, 'Failed to start AWS liveness session. Please try again.') }));
            });
    }, [props.personUuid, searchParams, isMobile, sourceId]);

    const clearLivenessTimeout = React.useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const handleLivenessTimeout = React.useCallback(() => {
        clearLivenessTimeout();
        setState(prev => ({
            ...prev,
            error: new Error('Liveness verification session expired. Please try again.')
        }));
    }, [clearLivenessTimeout]);

    React.useEffect(() => {
        // Prevent duplicate API calls
        if (hasFetched.current) return;
        hasFetched.current = true;
        
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
        if (!API_BASE_URL) {
            throw new Error('Environment variable REACT_APP_API_BASE_URL is not defined. Please configure it before running the application.');
        }
        
        // Set up timeout for liveness session
        timeoutRef.current = setTimeout(handleLivenessTimeout, LIVENESS_TIMEOUT_MS);
        
        if (searchParams.get("source") === 'qr') {
            const livenessSession = searchParams.get("livenessSession") || '';
            
            // Fetch liveness data from API using the session ID
            authenticationUseCase.fetchLivenessSession(livenessSession)
                .then((result) => {
                    setState({
                        data: {
                            vendor: result.data.vendor,
                            liveness_session: result.data.liveness_session,
                            person_uuid: result.data.person_uuid,
                            vendor_data: result.data.vendor_data,
                            trace_id: result.data.trace_id,
                        },
                        loading: false,
                        error: null
                    });
                })
                .catch((error) => {
                    const livenessError = ensureError(error, 'Failed to load liveness session. Please try again.');
                    setState(prev => ({ 
                        ...prev, 
                        loading: false, 
                        error: new Error(`Session Error: ${livenessError.message}`)
                    }));
                });
        } else {
            if (!props.personUuid) {
            setState(prev => ({
                ...prev,
                loading: false,
                error: new Error('Person UUID is required to start liveness verification.'),
            }));
            return;
        }

        // Fetch liveness data from API only once on mount
            const body = buildCreateSessionBody({
                personUuid: props.personUuid || '',
                isMobile,
                sourceId,
                searchParams,
            });
            fetch(
                `${API_BASE_URL}/persons/api/liveness`,
                {
                    method: "POST",
                    redirect: "follow",
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }
            )
                .then((response) => {
                    if (!response.ok) {
                        if (response.status === 404) {
                            throw new Error('Liveness session not found. Please start over.');
                        } else if (response.status === 400) {
                            throw new Error('Invalid request. Please check your connection and try again.');
                        } else if (response.status === 500) {
                            throw new Error('Server error. Please try again later.');
                        } else {
                            throw new Error(`Failed to fetch liveness data (${response.status}). Please try again.`);
                        }
                    }
                    return response.json();
                })
                .then((result) => {
                    reportEvent('LIVENESS_SESSION_STARTED', {
                        event:          'LIVENESS_SESSION_STARTED',
                        vendor:         result.data.vendor,
                        livenessSession: result.data.liveness_session,
                        personUuid:     result.data.person_uuid,
                        traceId:        result.data.trace_id,
                        timestamp:      new Date().toISOString(),
                    }, LogType.INFO);

                    setState({
                        data: {
                            vendor: result.data.vendor,
                            liveness_session: result.data.liveness_session,
                            person_uuid: result.data.person_uuid,
                            vendor_data: result.data.vendor_data,
                            trace_id: result.data.trace_id,
                        },
                        loading: false,
                        error: null
                    });
                    if (!isMobile) {
                        // Mark this session as a cross-device QR flow so the
                        // polling branch in validateCompletion is allowed to run.
                        isQrFlowRef.current = true;
                        // Build QR URL using the URL API to guarantee correct ?/& separators
                        const qrPageUrl = new URL('/', window.location.origin);
                        searchParams.forEach((value, key) => qrPageUrl.searchParams.set(key, value));
                        qrPageUrl.searchParams.set('source', 'qr');
                        qrPageUrl.searchParams.set('livenessSession', result.data.liveness_session);
                        const qrUrl = qrPageUrl.toString();
                        setQrLinkUrl(qrUrl);

                        QRCode.toDataURL(qrUrl, {
                            width: 300,
                            margin: 2,
                            color: {
                                dark: '#000000',
                                light: '#FFFFFF'
                            }
                        })
                            .then((url: string) => {
                                setQrCodeUrl(url);
                            })
                            .catch((err: Error) => {
                                console.error('Error generating QR code:', err);
                                setState(prev => ({
                                    ...prev,
                                    error: new Error(`Failed to generate QR code: ${err.message || 'Unknown error'}`)
                                }));
                            });
                    }
                })
                .catch((error) => {
                    const fetchError = ensureError(error, 'Failed to initialize liveness verification. Please try again.');
                    setState(prev => ({ 
                        ...prev, 
                        loading: false, 
                        error: new Error(`Initialization Error: ${fetchError.message}`)
                    }));
                });
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, props.personUuid, searchParams, sourceId]); // Removed validateCompletion to prevent circular dependency

    // Set up interval for QR code validation (desktop only)
    React.useEffect(() => {
        if (!state.data || isMobile || !qrCodeUrl) return;

        intervalRef.current = setInterval(() => {
            validateCompletion();

        }, 2000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [state.data, isMobile, qrCodeUrl, validateCompletion]);

    // Handle liveness verification in useEffect to prevent multiple calls
    React.useEffect(() => {
        validateCompletion();
    }, [liveness, state.data, livenessData, props, authenticationUseCase, validateCompletion]);

    // Cleanup interval on unmount
    React.useEffect(() => {
        return () => {
            const currentInterval = intervalRef.current;
            if (currentInterval) {
                clearInterval(currentInterval);
            }
            clearLivenessTimeout();
        };
    }, [clearLivenessTimeout]);

    // Show exit view on phone (QR secondary view) after liveness completes
    if (showExitView) {
        return <ExitView isError={exitViewIsError} />;
    }

    // Show error UI if fetch failed
    if (state.error) {
        return (
            <>
                <style>{`
                    @keyframes errorFadeIn {
                        from { opacity: 0; transform: translateY(16px); }
                        to   { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes errorIconIn {
                        from { opacity: 0; transform: scale(0.6); }
                        to   { opacity: 1; transform: scale(1); }
                    }
                    .error-card { animation: errorFadeIn 0.5s ease-out forwards; }
                    .error-icon { animation: errorIconIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s both; }
                `}</style>
                <div style={{
                    width: '100%',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#F9FAFB',
                    padding: '24px',
                    boxSizing: 'border-box',
                }}>
                    <div className="error-card" style={{
                        backgroundColor: 'white',
                        borderRadius: '20px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                        padding: '48px 40px',
                        maxWidth: '400px',
                        width: '100%',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}>
                        <div className="error-icon" style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: '#FEF2F2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '28px',
                            fontSize: '40px',
                        }}>
                            ⚠️
                        </div>
                        <h1 style={{
                            margin: '0 0 12px 0',
                            fontSize: '22px',
                            fontWeight: '700',
                            color: '#111827',
                            lineHeight: '1.3',
                        }}>
                            Error
                        </h1>
                        <p style={{
                            margin: '0 0 24px 0',
                            fontSize: '15px',
                            lineHeight: '1.6',
                            color: '#6B7280',
                        }}>
                            {state.error.message || 'An error occurred while loading liveness data.'}
                        </p>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            backgroundColor: '#FEF2F2',
                            borderRadius: '12px',
                            padding: '14px 20px',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}>
                            <span style={{ fontSize: '20px' }}>🔄</span>
                            <span style={{
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#374151',
                                textAlign: 'left',
                                lineHeight: '1.4',
                            }}>
                                Please refresh the page and try again.
                            </span>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (!state.data) {
        return (
            <div className="liveness-v2-spinner">
                <div className="liveness-v2-spinner__ring" aria-hidden="true" />
                <p className="liveness-v2-spinner__label">Loading...</p>
            </div>
        );
    }

    if (Object.keys(livenessData).length > 0 || (liveness && liveness.liveness)) {
        return (
            <div className="liveness-v2-spinner">
                <div className="liveness-v2-spinner__ring" aria-hidden="true" />
                <p className="liveness-v2-spinner__label">Processing...</p>
            </div>
        );
    }

    // qrCodeUrl is only ever set during the desktop/non-mobile fetch path,
    // so this is the correct guard regardless of viewport changes after the fact.
    // Keeping QRLiveness mounted on resize prevents IncodeLiveness from
    // mounting mid-session and triggering a cross-origin SDK error.
    if (qrCodeUrl) {
        return <QRLiveness qrCodeUrl={qrCodeUrl} qrLinkUrl={qrLinkUrl} />;
    }
    
    return <VendorUi livenessState={state} isMobile={isMobile} onConnectionError={onRetryWithAws} />;
}